import { useState, useEffect, useCallback } from 'react';
import { Check, X, Clock, HelpCircle, Ban, MinusCircle } from 'lucide-react';
import { apiFetch } from './api';
import { useToast } from './ToastContext';
import { GlobalContext } from './GlobalContext';
import { useContext } from 'react';

/**
 * Every SMS sent to one phone number, plus the do-not-message switch.
 *
 * Keyed on the NUMBER, not a customer id — it is the only identifier customers,
 * sellers and brokers share, so this one component serves all three modals. That
 * also means it works for sellers and brokers before they can be messaged at
 * all, which is the point: the history is ready when the sending is.
 *
 * Renders nothing when there is no phone number to ask about, so it can be
 * dropped into a modal unconditionally.
 */

const STATUS = {
  delivered: { label: 'Delivered', icon: Check, className: 'text-green-600' },
  sent: { label: 'Sent', icon: Clock, className: 'text-gray-500' },
  queued: { label: 'Queued', icon: Clock, className: 'text-gray-500' },
  failed: { label: 'Failed', icon: X, className: 'text-red-600' },
  rejected: { label: 'Rejected', icon: X, className: 'text-red-600' },
  unknown: { label: 'Unknown', icon: HelpCircle, className: 'text-amber-600' },
  skipped: { label: 'Not sent', icon: MinusCircle, className: 'text-gray-400' },
};

function when(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) +
         ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export default function RecipientMessageHistory({ phone, name }) {
  const { can } = useContext(GlobalContext) || {};
  const { showToast } = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!phone) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`/messages/recipient?number=${encodeURIComponent(phone)}`, { method: 'GET' });
      const body = await res.json();
      if (!res.ok || body.success === false) throw new Error(body.error || 'Could not load message history');
      setData(body);
    } catch (err) {
      setError(err.message || 'Could not load message history');
    } finally {
      setLoading(false);
    }
  }, [phone]);

  useEffect(() => { load(); }, [load]);

  const toggleOptOut = async () => {
    const optedOut = data?.optOut?.optedOut;
    setSaving(true);
    try {
      // DELETE carries the number in the query string rather than a body: a
      // DELETE with a body is handled inconsistently by proxies and is worth
      // avoiding for a one-parameter call.
      const res = optedOut
        ? await apiFetch(`/messages/recipient/opt-out?number=${encodeURIComponent(phone)}`, { method: 'DELETE' })
        : await apiFetch('/messages/recipient/opt-out', { method: 'POST', body: { number: phone } });
      const body = await res.json();
      if (!res.ok || body.success === false) throw new Error(body.error || 'Could not update');
      showToast('success', optedOut
        ? `${name || 'This contact'} can be messaged again.`
        : `${name || 'This contact'} will no longer be messaged.`);
      load();
    } catch (err) {
      showToast('error', err.message || 'Could not update the messaging preference');
    } finally {
      setSaving(false);
    }
  };

  if (!phone) return null;

  const optedOut = data?.optOut?.optedOut;
  const platformWide = data?.optOut?.platformWide;
  const atLimit = data && data.monthlyCap > 0 && data.sentThisMonth >= data.monthlyCap;

  return (
    <div id="recipient-message-history" className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-medium">Message history</h4>
          {data && (
            <p className="text-xs text-gray-500">
              {data.monthlyCap > 0
                ? `${data.sentThisMonth} of ${data.monthlyCap} messages this month`
                : `${data.sentThisMonth} message${data.sentThisMonth === 1 ? '' : 's'} this month`}
            </p>
          )}
        </div>

        {/* Hidden entirely from users who cannot send — a read-only viewer has
            no business changing whether someone gets messaged. */}
        {can && can('messages.send') && data && (
          <button
            type="button"
            className={`btn-t ${optedOut ? 'btn-t-secondary' : 'btn-t-ghost'}`}
            onClick={toggleOptOut}
            disabled={saving || platformWide}
            title={platformWide
              ? 'This number opted out of all messages from the platform and cannot be re-enabled here.'
              : optedOut ? 'Allow messages to this number again' : 'Stop sending messages to this number'}
          >
            <Ban size={14} />
            <span>{optedOut ? 'Opted out' : 'Do not message'}</span>
          </button>
        )}
      </div>

      {optedOut && (
        <div
          className="text-xs rounded-md px-3 py-2"
          style={{ background: 'var(--theme-warning-tint)', color: 'var(--theme-warning-ink)' }}
        >
          {platformWide
            ? 'This number has opted out of all messages from the platform. Sends will skip them.'
            : 'This number is set to do-not-message. Sends will skip them.'}
        </div>
      )}

      {!optedOut && atLimit && (
        <div
          className="text-xs rounded-md px-3 py-2"
          style={{ background: 'var(--theme-warning-tint)', color: 'var(--theme-warning-ink)' }}
        >
          This number has reached its monthly message limit. Further sends will skip them until the 1st.
        </div>
      )}

      {loading && <p className="text-xs text-gray-500">Loading…</p>}
      {error && <p className="text-xs text-red-600">{error}</p>}

      {data && !loading && data.messages.length === 0 && (
        <p className="text-xs text-gray-500">No messages have been sent to this number.</p>
      )}

      {data && data.messages.length > 0 && (
        <div className="border rounded-md divide-y max-h-64 overflow-y-auto">
          {data.messages.map((m) => {
            const s = STATUS[m.status] || STATUS.unknown;
            const Icon = s.icon;
            // skip_reason and failure_reason are different things and are kept
            // that way: one is a decision we made, the other is the provider
            // reporting a problem.
            const detail = m.skip_reason || m.failure_reason;
            return (
              <div key={m.id} className="p-2">
                <div className="flex items-start justify-between gap-3">
                  <span className="text-xs text-gray-500">
                    {when(m.batch_sent_at)}
                    {m.template_name ? ` · ${m.template_name}` : ''}
                    {m.sheet_name ? ` · ${m.sheet_name}` : ''}
                  </span>
                  <span className={`text-xs flex items-center gap-1 shrink-0 ${s.className}`}>
                    <Icon size={12} />
                    {s.label}
                  </span>
                </div>
                <p className="text-xs mt-1 whitespace-pre-wrap">{m.message}</p>
                {detail && <p className="text-xs mt-1 text-gray-500">{detail}</p>}
                {m.sent_by && <p className="text-[11px] mt-1 text-gray-400">Sent by {m.sent_by}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
